/**
 * @file   src\pages\Messaging.js
 * @brief  This file is responsible for handling messaging/chat screen.
 * @date   June, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */

import React, { useState, useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { useSelector } from "react-redux";
import MessageLeftSection from "../components/Messages/MessageLeftSection";
import { decodeParam, getLocalStorage, loggedInUser } from "../helpers/common";
import { USER_INFO_KEY } from "../constants/common";
import MessagesRightSection from "../components/Messages/MessagesRightSection";
import { loginOrSignUp } from "../helpers/message";
import { getIntroductionDetaisById } from "../services/introductionService";
import IntroDetails from "../components/Messages/IntroDetails";
import AgentProfileViewModal from "../components/Introduction/AgentProfileViewModal";
import SelectDropDown from "../components/Common/SelectDropDown";
import {
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { pageURLs } from "@/constants";
import DirectMessages from "./v2/DirectMessaging";
import { PageWrapper } from "@/components/PageWrapper";
import { useMLSAgentAtGitchaHomeRedirect } from "@/hooks/useMLSAgentAtGitchaRedirect";

const INTRO_DETAILS_KEY = "introductionDetails";

// Shape the filter dropdown falls back to before any conversation has loaded.
const EMPTY_FILTER_OPTIONS = [
  { value: "all", label: 0 },
  { value: "intro_sent", label: 0 },
  { value: "intro_to_me", label: 0 },
];

const Messaging = (props) => {

  const navigate = useNavigate();

  const sessionData = getLocalStorage(USER_INFO_KEY);
  //user's own info
  const userId = _.get(sessionData, "userInfo.userId", "");
  const [selectedIntroductionId, setSelectedIntroductionId] = useState(0);
  const [selectedData, setSelectedData] = useState("");
  const [showChatOnMobile, setShowChatOnMobile] = useState(false);
  const [filter, setFilter] = useState("all");
  const userDetails = loggedInUser();
  // The intro-details modal is opened by request, but only actually shows once
  // the query behind it has data - same as the old fetch-then-open callback.
  const [introDetailsRequested, setIntroDetailsRequested] = useState(false);
  const [introDetailsId, setIntroDetailsId] = useState(null);
  const handleIntroDetailModal = (modalState) =>
    setIntroDetailsRequested(!!modalState);
  const [showProfileModal, setProfileModal] = useState(false);
  const ProfileModalClose = () => setProfileModal(false);
  const [selectedUserId, setSelectedUserId] = useState(0);
  const [isFromMessage, setIsFromMessage] = useState(false);

  const [searchParams] = useSearchParams();
  const [introIdFromURL, setIntroIDFromURL] = useState(
    searchParams.get("Id") ? decodeParam(searchParams.get("Id")) : null,
  );

  const [userDeleted, setUserDeleted] = useState(false);

  const chatMessages = useSelector((state) => state.unreadCount.chatMessages);
  const location = useLocation();
  const introductionsArray = location.state?.introductionsArray || [];
  const fromDM = location.state?.fromDM;

  useEffect(() => {
    let email = _.get(userDetails, "userInfo.email", null);
    let login = loginOrSignUp(email);

    if (!login) {
      navigate(pageURLs.home);
    }
  }, []);
  const handleWantAdSelection = async (msgData) => {
    if (msgData?.introductionId) {
      setSelectedIntroductionId(msgData?.introductionId);
      setSelectedData(msgData);
      setIntroIDFromURL(null);
      setShowChatOnMobile(true);
    }
  };

  const handleFilter = (e) => {
    setFilter(e.value);
  };

  const handleFilterValue = (value) => {
    if (filterList && value) {
      const filterValue = filterList.filter(function (lst) {
        return lst.value === value;
      });
      return filterValue;
    }
    return null;
  };

  // Ordering is derived from the Firestore-backed Redux payload rather than
  // copied into state by an effect, so the list can't lag a render behind the
  // filter that produced it.
  const lastOrderedRef = useRef([]);

  const { filterList, message } = useMemo(() => {
    const fetchedMessages = chatMessages?.payload;

    if (!fetchedMessages) {
      lastOrderedRef.current = [];
      return { filterList: EMPTY_FILTER_OPTIONS, message: [] };
    }

    const isLive = (item) => item.isaccepted == true && item.isdeleted == false;
    const assignedToMe = fetchedMessages.filter(
      (item) => item.wantAdCreatedId == userId && isLive(item),
    );
    const assignedByMe = fetchedMessages.filter(
      (item) => item.inventroyCreatedId == userId && isLive(item),
    );
    const all = [...assignedToMe, ...assignedByMe];

    const options = [
      {
        value: "all",
        label: all.length
          ? "All conversations (" + all.length + ")"
          : "All conversations",
      },
      {
        value: "intro_sent",
        label: assignedByMe.length
          ? "Introductions sent (" + assignedByMe.length + ")"
          : "Introductions sent",
      },
      {
        value: "intro_to_me",
        label: assignedToMe.length
          ? "Introduced to me (" + assignedToMe.length + ")"
          : "Introduced to me",
      },
    ];

    const source =
      filter == "intro_to_me"
        ? assignedToMe
        : filter == "intro_sent"
          ? assignedByMe
          : all;

    // Firestore's serverTimestamp() reads back null on the writing client
    // until the server confirms it. Sorting mid-write would jumble the list,
    // so hold the last good ordering until every row has a timestamp - the
    // behaviour the previous effect got by skipping its setState.
    if (!source.every((o) => o.createdat)) {
      return { filterList: options, message: lastOrderedRef.current };
    }

    const ordered = [...source].sort((a, b) => b.createdat - a.createdat);
    lastOrderedRef.current = ordered;
    return { filterList: options, message: ordered };
  }, [chatMessages, filter, userId]);

  // Clearing the open conversation is a real state change, not derived data,
  // so it stays an effect.
  const hasConversations = !!chatMessages?.payload;
  useEffect(() => {
    if (!hasConversations) {
      setSelectedData(null);
      setSelectedIntroductionId(null);
    }
  }, [hasConversations]);

  // Introduction details for the modal. Cached per introduction, so reopening
  // one the agent already viewed is instant instead of re-fetching.
  const introDetailsQuery = useQuery({
    queryKey: [INTRO_DETAILS_KEY, introDetailsId],
    queryFn: async () => {
      const response = await getIntroductionDetaisById(introDetailsId);
      const responseResult = _.get(response, "result", null);
      if (response?.errorCode !== 0 || !responseResult) {
        throw new Error(
          response?.message || "Could not load the introduction details",
        );
      }
      return responseResult[0];
    },
    enabled: !!introDetailsId,
  });

  const introDetails = introDetailsQuery.data ?? [];
  // Matches the old callback: the modal only appeared once the fetch resolved
  // with a result, and stayed shut on failure.
  const showViewIntroductionModal =
    introDetailsRequested && !!introDetailsQuery.data;

  const getIntroductionDetails = (introductionId) => {
    if (!introductionId) return;
    setIntroDetailsId(introductionId);
    setIntroDetailsRequested(true);
  };

  const ViewProfile = (agentId, isFromMessageScreen = false) => {
    handleIntroDetailModal(false);
    setIsFromMessage(isFromMessageScreen);
    setSelectedUserId(agentId);
    setProfileModal(true);
  };

  const closeAgentModal = (isFromMessage) => {
    if (!isFromMessage) {
      handleIntroDetailModal(true);
    }
    setProfileModal(false);
  };

  useMLSAgentAtGitchaHomeRedirect();

  return (
    <>
      <PageWrapper>
        <div className="m-0">
          <div className="main-content p-0 agent-no-login-content">
            <div className="msg-tab">
              <Tabs
                defaultActiveKey="messages"
                id="uncontrolled-tab-example"
                className="mb-3"
                onSelect={(key) => {
                  if (key === "direct-message") {
                    navigate("/direct-messages");
                  } else {
                    navigate("/messages");
                  }
                }}
              >
                <Tab eventKey="messages" title="Introduction Messages">
                  <div className={`row msg-height ${showChatOnMobile ? "mobile-chat-active" : ""}`}>
                    <div className="col-12 col-md-6 pe-md-3 col-lg-4 pe-lg-3 col-xl-4 pe-xl-2 mb-4 mb-sm-4 mb-md-0 msg-left-col">
                      <div className="ad-strengthen-wrap strengthen-adjust-wrap mh-490 pe-0 ps-0 pt-0 pb-2">
                        <div className="messaging-top-header pb-12 pl-16 pr-16 pt-4">
                          <h3>Messages</h3>
                          {!introductionsArray.length > 0 && !fromDM && (
                            <div className="messaging-select-wrap">
                              <SelectDropDown
                                id="multi3444"
                                name="colors33"
                                options={filterList}
                                className="basic-single-inventory bg-color-fix message-custom-width mb-0"
                                classNamePrefix="select"
                                placeholder="Select"
                                defaultValue={filterList[0]}
                                value={handleFilterValue(filter)}
                                onChange={(e) => {
                                  handleFilter(e);
                                }}
                                styles={{
                                  dropdownIndicator: (provided, state) => ({
                                    ...provided,
                                    transform:
                                      state.selectProps.menuIsOpen &&
                                      "rotate(180deg)",
                                  }),
                                }}
                                errorClass="error-msg error-msg-height"
                              />
                            </div>
                          )}
                        </div>

                        <MessageLeftSection
                          userId={userId}
                          messages={message}
                          selectedIntroductionId={selectedIntroductionId}
                          handleWantAdSelection={(msgData) => {
                            handleWantAdSelection(msgData);
                          }}
                          introIdFromURL={introIdFromURL}
                          introductionsArray={introductionsArray}
                          fromDM={fromDM}
                        />
                      </div>
                    </div>
                    {(selectedData?.introductionId || introductionsArray.length > 0) && (
                      <MessagesRightSection
                        selectedId={selectedIntroductionId}
                        userId={userId}
                        selectedData={selectedData}
                        getIntroductionDetails={(IntroductionId) => {
                          getIntroductionDetails(IntroductionId);
                        }}
                        ViewProfileModal={(agentId) => {
                          ViewProfile(agentId, true);
                        }}
                        userDeletedUpdate={(deleted) => {
                          setUserDeleted(deleted);
                        }}
                        introductionsArray={introductionsArray}
                        fromDM={fromDM}
                        onMobileBack={() => {
                          setShowChatOnMobile(false);
                          setSelectedData(null);
                          setSelectedIntroductionId(null);
                        }}
                      />
                    )}
                  </div>
                </Tab>
                <Tab eventKey="direct-message" title="Direct Messages">
                  <DirectMessages />
                </Tab>
              </Tabs>
            </div>
          </div>
        </div>
      </PageWrapper>
      <IntroDetails
        details={introDetails}
        show={showViewIntroductionModal}
        onHide={handleIntroDetailModal}
        ViewProfileModal={(agentId) => {
          ViewProfile(agentId);
        }}
        userDeleted={userDeleted}
      />

      <AgentProfileViewModal
        show={showProfileModal}
        onHide={ProfileModalClose}
        userId={selectedUserId}
        isFromMessage={isFromMessage}
        onClose={(isFromMessage) => {
          closeAgentModal(isFromMessage);
        }}
      />
    </>
  );
};

export default Messaging;
